import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useCepLookup } from '../../hooks';
import { PageHeader, InputMask } from '../../components/ui';
import { Panel, buttonClass } from '../../design-system';
import type { TipoServico } from '../../types';

const fornecedorSchema = z.object({
  nome: z.string().min(1, 'Nome/Razão Social é obrigatório'),
  tipoPessoa: z.enum(['PF', 'PJ']),
  cpfCnpj: z.string().optional(),
  rgIe: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  telefone: z.string().optional(),
  celular: z.string().optional(),
  whatsapp: z.string().optional(),
  enderecoCep: z.string().optional(),
  enderecoLogradouro: z.string().optional(),
  enderecoNumero: z.string().optional(),
  enderecoComplemento: z.string().optional(),
  enderecoBairro: z.string().optional(),
  enderecoCidade: z.string().optional(),
  enderecoUf: z.string().optional(),
  observacoes: z.string().optional(),
  servicoIds: z.array(z.number()).default([]),
});

type FornecedorFormData = z.infer<typeof fornecedorSchema>;

export function FornecedorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tiposServico, setTiposServico] = useState<TipoServico[]>([]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FornecedorFormData>({
    resolver: zodResolver(fornecedorSchema),
    defaultValues: {
      tipoPessoa: 'PJ',
      servicoIds: [],
    },
  });

  const tipoPessoa = watch('tipoPessoa');
  const servicoIds = watch('servicoIds');

  // Hook para busca de CEP
  const { fetchCep } = useCepLookup({
    onSuccess: (data) => {
      setValue('enderecoLogradouro', data.logradouro);
      setValue('enderecoBairro', data.bairro);
      setValue('enderecoCidade', data.localidade);
      setValue('enderecoUf', data.uf);
    },
  });

  // Carregar tipos de serviço
  useEffect(() => {
    const controller = new AbortController();
    api.get<TipoServico[]>('/fornecedores/tipos-servico', { signal: controller.signal })
      .then((response) => setTiposServico(response.data))
      .catch(() => { /* Ignore */ });
    return () => controller.abort();
  }, []);

  // Carregar fornecedor para edição
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);
    api.get<Record<string, unknown>>(`/fornecedores/${id}`, { signal: controller.signal })
      .then((response) => {
        const fornecedor = response.data;
        Object.keys(fornecedor).forEach((key) => {
          const schemaKey = key.charAt(0).toLowerCase() + key.slice(1);
          if (schemaKey in fornecedorSchema.shape) {
            setValue(schemaKey as keyof FornecedorFormData, (fornecedor[key] ?? '') as FornecedorFormData[keyof FornecedorFormData]);
          }
        });
      })
      .catch((error) => {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        showError('Erro ao carregar fornecedor');
      })
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [id, setValue, showError]);

  const toggleServico = (servicoId: number) => {
    const current = servicoIds || [];
    if (current.includes(servicoId)) {
      setValue('servicoIds', current.filter((id) => id !== servicoId));
    } else {
      setValue('servicoIds', [...current, servicoId]);
    }
  };

  const onSubmit = async (data: FornecedorFormData) => {
    setIsSaving(true);
    try {
      if (isEditing) {
        await api.put(`/fornecedores/${id}`, data);
      } else {
        await api.post('/fornecedores', data);
      }

      showSuccess(isEditing ? 'Fornecedor atualizado com sucesso' : 'Fornecedor cadastrado com sucesso');
      navigate('/fornecedores');
    } catch (error: unknown) {
      console.error('Erro ao salvar fornecedor:', error);
      const err = error as { response?: { data?: { message?: string; title?: string; errors?: Record<string, string[]> } } };
      let message = 'Erro ao salvar fornecedor';
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.response?.data?.title) {
        message = err.response.data.title;
        if (err.response.data.errors) {
          const firstError = Object.values(err.response.data.errors)[0];
          if (firstError?.[0]) {
            message += `: ${firstError[0]}`;
          }
        }
      }
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Agrupar tipos de serviço por categoria
  const tiposPorCategoria = {
    CUSTO: tiposServico.filter((t) => t.categoria === 'CUSTO' || t.categoria === 'AMBOS'),
    PENDENCIA: tiposServico.filter((t) => t.categoria === 'PENDENCIA' || t.categoria === 'AMBOS'),
  };

  return (
    <div>
      <PageHeader
        title={isEditing ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        subtitle={isEditing ? 'Altere os dados do fornecedor' : 'Cadastre um novo fornecedor'}
        showBack
        backTo="/fornecedores"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados Básicos */}
        <Panel title="Dados Básicos">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                {tipoPessoa === 'PJ' ? 'Razão Social *' : 'Nome *'}
              </label>
              <input
                type="text"
                {...register('nome')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-rose-600">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Tipo de Pessoa
              </label>
              <select
                {...register('tipoPessoa')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="PJ">Pessoa Jurídica</option>
                <option value="PF">Pessoa Física</option>
              </select>
            </div>

            <Controller
              name="cpfCnpj"
              control={control}
              render={({ field }) => (
                <InputMask
                  label={tipoPessoa === 'PJ' ? 'CNPJ' : 'CPF'}
                  mask={tipoPessoa === 'PJ' ? 'cnpj' : 'cpf'}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={tipoPessoa === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                />
              )}
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                {tipoPessoa === 'PJ' ? 'Inscrição Estadual' : 'RG'}
              </label>
              <input
                type="text"
                {...register('rgIe')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>
        </Panel>

        {/* Serviços Oferecidos */}
        <Panel title="Serviços Oferecidos">
          <p className="mb-4 text-sm text-slate-500">
            Selecione os tipos de serviço que este fornecedor oferece. Isso ajudará a filtrar fornecedores ao registrar custos e pendências.
          </p>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Serviços de Custo */}
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                <span className="h-2 w-2 rounded-full bg-rose-500"></span>
                Serviços de Custo
              </h3>
              <div className="space-y-2">
                {tiposPorCategoria.CUSTO.map((tipo) => (
                  <label
                    key={tipo.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                      servicoIds?.includes(tipo.id)
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={servicoIds?.includes(tipo.id) || false}
                      onChange={() => toggleServico(tipo.id)}
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">{tipo.nome}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Serviços de Pendência */}
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                <span className="h-2 w-2 rounded-full bg-amber-500"></span>
                Serviços de Pendência
              </h3>
              <div className="space-y-2">
                {tiposPorCategoria.PENDENCIA.filter((t) => !tiposPorCategoria.CUSTO.some((c) => c.id === t.id)).map((tipo) => (
                  <label
                    key={tipo.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                      servicoIds?.includes(tipo.id)
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={servicoIds?.includes(tipo.id) || false}
                      onChange={() => toggleServico(tipo.id)}
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">{tipo.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        {/* Contato */}
        <Panel title="Contato">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                placeholder="email@empresa.com"
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-rose-600">{errors.email.message}</p>
              )}
            </div>

            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="Telefone"
                  mask="phone"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="(00) 0000-0000"
                />
              )}
            />

            <Controller
              name="celular"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="Celular"
                  mask="cellphone"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="(00) 00000-0000"
                />
              )}
            />

            <Controller
              name="whatsapp"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="WhatsApp"
                  mask="cellphone"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="(00) 00000-0000"
                />
              )}
            />
          </div>
        </Panel>

        {/* Endereço */}
        <Panel title="Endereço">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Controller
              name="enderecoCep"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="CEP"
                  mask="cep"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => field.value && fetchCep(field.value)}
                  placeholder="00000-000"
                />
              )}
            />

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Logradouro
              </label>
              <input
                type="text"
                {...register('enderecoLogradouro')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Número
              </label>
              <input
                type="text"
                {...register('enderecoNumero')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Complemento
              </label>
              <input
                type="text"
                {...register('enderecoComplemento')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Bairro
              </label>
              <input
                type="text"
                {...register('enderecoBairro')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Cidade
              </label>
              <input
                type="text"
                {...register('enderecoCidade')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                UF
              </label>
              <select
                {...register('enderecoUf')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Observações */}
        <Panel title="Observações">
          <textarea
            {...register('observacoes')}
            rows={4}
            placeholder="Observações sobre o fornecedor..."
            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/fornecedores')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={buttonClass('primary')}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
